/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';
import './App.css';
import { Home } from './layout/Home';
import { usePrivateRoute } from '@ink-ai/portal/common/hooks/usePrivateRoute';
import { useAutoRefreshToken } from '../common/hooks/useAutoRefreshToken';
import { useSyncSelectedData } from '../common/hooks/useSyncSelectedData';
import { SocketLoading } from '../common/socket/SocketLoading';

/**
 * Shown only while a returning user's session is being restored silently. It
 * deliberately carries no application content — the point of the gate is that a
 * signed-out visitor learns nothing about the app.
 */
const AuthPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '100vh',
      }}
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        {t('common.authenticating')}
      </Typography>
    </Box>
  );
};

function App() {
  const { ready, resolving } = usePrivateRoute();
  useAutoRefreshToken();
  useSyncSelectedData();

  // Never paint the authenticated shell before auth is settled. The route guard
  // works through effects and an async silent re-auth, so rendering <Home />
  // unconditionally showed the nav, the greeting and page content to signed-out
  // visitors until the redirect to /login fired.
  if (!ready) {
    return (
      <div className="App">
        {resolving ? <AuthPlaceholder /> : null}
      </div>
    );
  }

  return (
    <div className="App">
      <Home />
      {/* <SocketLoading variant="modal" /> */}
    </div>
  );
}

export default App;
